import React, {useContext} from 'react';
import {defineMessages, useIntl} from 'react-intl';
import {
    type ButtonColumn,
    type CurrencyColumn,
    type DataGridColumn,
    DataGridServer,
    type DataGridServerProps,
    type TextColumn,
} from '@igp/shared/components/data-grid';
import {ModalContext} from '@igp/shared/components/modal';
import {formatTimestamp} from '@igp/shared/utils';

import type {AgentLevelReportV2ViewModel, DataGridAgentLevelReportV2Keys, QueryType} from '../types';

import {RevenueShareDetails} from './RevenueShareDetails';

export const localized = defineMessages<DataGridAgentLevelReportV2Keys>({
    agentUid: {
        id: 'useDataGridAgentLevelReportV2Columns_agentUid',
        defaultMessage: 'Agent ID',
    },
    agentUsername: {
        id: 'useDataGridAgentLevelReportV2Columns_agentUsername',
        defaultMessage: 'Username',
    },
    casinoCommissionableAmount: {
        id: 'useDataGridAgentLevelReportV2Columns_casinoCommissionableAmount',
        defaultMessage: 'Casino Commissionable Amount',
    },
    casinoCosts: {
        id: 'useDataGridAgentLevelReportV2Columns_casinoCosts',
        defaultMessage: 'Casino Costs',
    },
    casinoGgr: {
        id: 'useDataGridAgentLevelReportV2Columns_casinoGgr',
        defaultMessage: 'Casino GGR',
    },
    casinoNetShare: {
        id: 'useDataGridAgentLevelReportV2Columns_casinoNetShare',
        defaultMessage: 'Casino',
    },
    casinoNgr: {
        id: 'useDataGridAgentLevelReportV2Columns_casinoNgr',
        defaultMessage: 'Casino NGR',
    },
    casinoShareRatioPerc: {
        id: 'useDataGridAgentLevelReportV2Columns_casinoShareRatioPerc',
        defaultMessage: 'Casino Share Ratio Perc',
    },
    globalSpinsCommissionableAmount: {
        id: 'useDataGridAgentLevelReportV2Columns_globalSpinsCommissionableAmount',
        defaultMessage: 'Global Spins Commissionable Amount',
    },
    globalSpinsCosts: {
        id: 'useDataGridAgentLevelReportV2Columns_globalSpinsCosts',
        defaultMessage: 'Global Spins Costs',
    },
    globalSpinsGgr: {
        id: 'useDataGridAgentLevelReportV2Columns_globalSpinsGgr',
        defaultMessage: 'Global Spins GGR',
    },
    globalSpinsNetShare: {
        id: 'useDataGridAgentLevelReportV2Columns_globalSpinsNetShare',
        defaultMessage: 'Global Spins',
    },
    globalSpinsNgr: {
        id: 'useDataGridAgentLevelReportV2Columns_globalSpinsNgr',
        defaultMessage: 'Global Spins NGR',
    },
    globalSpinsShareRatioPerc: {
        id: 'useDataGridAgentLevelReportV2Columns_globalSpinsShareRatioPerc',
        defaultMessage: 'Global Spins Share Ratio Perc',
    },
    pokerCashPloCommissionableAmount: {
        id: 'useDataGridAgentLevelReportV2Columns_pokerCashPloCommissionableAmount',
        defaultMessage: 'Poker Cash Plo Commissionable Amount',
    },
    pokerCashPloCosts: {
        id: 'useDataGridAgentLevelReportV2Columns_pokerCashPloCosts',
        defaultMessage: 'Poker Cash Plo Costs',
    },
    pokerCashPloGgr: {
        id: 'useDataGridAgentLevelReportV2Columns_pokerCashPloGgr',
        defaultMessage: 'Poker Cash Plo GGR',
    },
    pokerCashPloNetShare: {
        id: 'useDataGridAgentLevelReportV2Columns_pokerCashPloNetShare',
        defaultMessage: 'Poker Cash (PLO)',
    },
    pokerCashPloNgr: {
        id: 'useDataGridAgentLevelReportV2Columns_pokerCashPloNgr',
        defaultMessage: 'Poker Cash Plo NGR',
    },
    pokerCashPloShareRatioPerc: {
        id: 'useDataGridAgentLevelReportV2Columns_pokerCashPloShareRatioPerc',
        defaultMessage: 'Poker Cash Plo Share Ratio Perc',
    },
    pokerMttCommissionableAmount: {
        id: 'useDataGridAgentLevelReportV2Columns_pokerMttCommissionableAmount',
        defaultMessage: 'Poker MTT Commissionable Amount',
    },
    pokerMttCosts: {
        id: 'useDataGridAgentLevelReportV2Columns_pokerMttCosts',
        defaultMessage: 'Poker MTT Costs',
    },
    pokerMttGgr: {
        id: 'useDataGridAgentLevelReportV2Columns_pokerMttGgr',
        defaultMessage: 'Poker MTT GGR',
    },
    pokerMttNetShare: {
        id: 'useDataGridAgentLevelReportV2Columns_pokerMttNetShare',
        defaultMessage: 'Poker MTT',
    },
    pokerMttNgr: {
        id: 'useDataGridAgentLevelReportV2Columns_pokerMttNgr',
        defaultMessage: 'Poker MTT NGR',
    },
    pokerMttShareRatioPerc: {
        id: 'useDataGridAgentLevelReportV2Columns_pokerMttShareRatioPerc',
        defaultMessage: 'Poker MTT Share Ratio Perc',
    },
    pokerNlheSdCommissionableAmount: {
        id: 'useDataGridAgentLevelReportV2Columns_pokerNlheSdCommissionableAmount',
        defaultMessage: 'Poker Nlhe Sd Commissionable Amount',
    },
    pokerNlheSdCosts: {
        id: 'useDataGridAgentLevelReportV2Columns_pokerNlheSdCosts',
        defaultMessage: 'Poker Nlhe Sd Costs',
    },
    pokerNlheSdGgr: {
        id: 'useDataGridAgentLevelReportV2Columns_pokerNlheSdGgr',
        defaultMessage: 'Poker Nlhe Sd Ggr',
    },
    pokerNlheSdNetShare: {
        id: 'useDataGridAgentLevelReportV2Columns_pokerNlheSdNetShare',
        defaultMessage: 'Poker Cash (NLHE SD)',
    },
    pokerNlheSdNgr: {
        id: 'useDataGridAgentLevelReportV2Columns_pokerNlheSdNgr',
        defaultMessage: 'Poker Nlhe Sd Ngr',
    },
    pokerNlheSdShareRatioPerc: {
        id: 'useDataGridAgentLevelReportV2Columns_pokerNlheSdShareRatioPerc',
        defaultMessage: 'Poker Nlhe Sd Share Ratio Perc',
    },
    rowId: {
        id: 'useDataGridAgentLevelReportV2Columns_rowId',
        defaultMessage: 'RowId',
    },
    sportsCommissionableAmount: {
        id: 'useDataGridAgentLevelReportV2Columns_sportsCommissionableAmount',
        defaultMessage: 'Sports Commissionable Amount',
    },
    sportsCosts: {
        id: 'useDataGridAgentLevelReportV2Columns_sportsCosts',
        defaultMessage: 'Sports Costs',
    },
    sportsGgr: {
        id: 'useDataGridAgentLevelReportV2Columns_sportsGgr',
        defaultMessage: 'Sports GGR',
    },
    sportsNetShare: {
        id: 'useDataGridAgentLevelReportV2Columns_sportsNetShare',
        defaultMessage: 'Sports',
    },
    sportsNgr: {
        id: 'useDataGridAgentLevelReportV2Columns_sportsNgr',
        defaultMessage: 'Sports NGR',
    },
    sportsShareRatioPerc: {
        id: 'useDataGridAgentLevelReportV2Columns_sportsShareRatioPerc',
        defaultMessage: 'Sports Share Ratio Perc',
    },
    totalNetShare: {
        id: 'useDataGridAgentLevelReportV2Columns_totalNetShare',
        defaultMessage: 'Total Revenue Share',
    },
    weekStartDateTs: {
        id: 'useDataGridAgentLevelReportV2Columns_weekStartDateTs',
        defaultMessage: 'Report Week Start',
    },
    details: {
        id: 'useDataGridAgentLevelReportV2Columns_details',
        defaultMessage: 'Details',
    },
});

export function useDataGridAgentLevelReportV2Columns(
    keys: DataGridAgentLevelReportV2Keys[],
    type: QueryType = 'weekly',
    startDate?: number,
    endDate?: number
): DataGridColumn<AgentLevelReportV2ViewModel>[] {
    const defaultColumn: DataGridColumn<AgentLevelReportV2ViewModel> = {
        width: 150,
        type: undefined,
        valueGetter: undefined,
        field: undefined,
    };
    const currency = 'USD';
    const agentUid: TextColumn<AgentLevelReportV2ViewModel> = {
        ...defaultColumn,
        type: 'text',
        valueGetter: m => m.agentUid,
        field: 'agentUid',
        title: localized.agentUid,
        sortable: true,
    };
    const agentUsername: TextColumn<AgentLevelReportV2ViewModel> = {
        ...defaultColumn,
        width: 200,
        type: 'text',
        valueGetter: m => m.agentUsername,
        field: 'agentUsername',
        title: localized.agentUsername,
        sortable: true,
    };
    const casinoCommissionableAmount: CurrencyColumn<AgentLevelReportV2ViewModel> = {
        ...defaultColumn,
        type: 'currency',
        valueGetter: m => ({amount: m.casinoCommissionableAmount, currency}),
        field: 'casinoCommissionableAmount',
        title: localized.casinoCommissionableAmount,
        sortable: true,
    };
    const casinoCosts: CurrencyColumn<AgentLevelReportV2ViewModel> = {
        ...defaultColumn,
        type: 'currency',
        valueGetter: m => ({amount: m.casinoCosts, currency}),
        field: 'casinoCosts',
        title: localized.casinoCosts,
        sortable: true,
    };
    const casinoGgr: CurrencyColumn<AgentLevelReportV2ViewModel> = {
        ...defaultColumn,
        type: 'currency',
        valueGetter: m => ({amount: m.casinoGgr, currency}),
        field: 'casinoGgr',
        title: localized.casinoGgr,
        sortable: true,
    };
    const casinoNetShare: CurrencyColumn<AgentLevelReportV2ViewModel> = {
        ...defaultColumn,
        type: 'currency',
        valueGetter: m => ({amount: m.casinoNetShare, currency}),
        field: 'casinoNetShare',
        title: localized.casinoNetShare,
        sortable: true,
    };
    const casinoNgr: CurrencyColumn<AgentLevelReportV2ViewModel> = {
        ...defaultColumn,
        type: 'currency',
        valueGetter: m => ({amount: m.casinoNgr, currency}),
        field: 'casinoNgr',
        title: localized.casinoNgr,
        sortable: true,
    };
    const casinoShareRatioPerc: CurrencyColumn<AgentLevelReportV2ViewModel> = {
        ...defaultColumn,
        type: 'currency',
        valueGetter: m => ({amount: m.casinoShareRatioPerc, currency}),
        field: 'casinoShareRatioPerc',
        title: localized.casinoShareRatioPerc,
        sortable: true,
    };
    const globalSpinsCommissionableAmount: CurrencyColumn<AgentLevelReportV2ViewModel> = {
        ...defaultColumn,
        type: 'currency',
        valueGetter: m => ({amount: m.globalSpinsCommissionableAmount, currency}),
        field: 'globalSpinsCommissionableAmount',
        title: localized.globalSpinsCommissionableAmount,
        sortable: true,
    };
    const globalSpinsCosts: CurrencyColumn<AgentLevelReportV2ViewModel> = {
        ...defaultColumn,
        type: 'currency',
        valueGetter: m => ({amount: m.globalSpinsCosts, currency}),
        field: 'globalSpinsCosts',
        title: localized.globalSpinsCosts,
        sortable: true,
    };
    const globalSpinsGgr: CurrencyColumn<AgentLevelReportV2ViewModel> = {
        ...defaultColumn,
        type: 'currency',
        valueGetter: m => ({amount: m.globalSpinsGgr, currency}),
        field: 'globalSpinsGgr',
        title: localized.globalSpinsGgr,
        sortable: true,
    };
    const globalSpinsNetShare: CurrencyColumn<AgentLevelReportV2ViewModel> = {
        ...defaultColumn,
        type: 'currency',
        valueGetter: m => ({amount: m.globalSpinsNetShare, currency}),
        field: 'globalSpinsNetShare',
        title: localized.globalSpinsNetShare,
        sortable: true,
    };
    const globalSpinsNgr: CurrencyColumn<AgentLevelReportV2ViewModel> = {
        ...defaultColumn,
        type: 'currency',
        valueGetter: m => ({amount: m.globalSpinsNgr, currency}),
        field: 'globalSpinsNgr',
        title: localized.globalSpinsNgr,
        sortable: true,
    };
    const globalSpinsShareRatioPerc: CurrencyColumn<AgentLevelReportV2ViewModel> = {
        ...defaultColumn,
        type: 'currency',
        valueGetter: m => ({amount: m.globalSpinsShareRatioPerc, currency}),
        field: 'globalSpinsShareRatioPerc',
        title: localized.globalSpinsShareRatioPerc,
        sortable: true,
    };
    const pokerCashPloCommissionableAmount: CurrencyColumn<AgentLevelReportV2ViewModel> = {
        ...defaultColumn,
        type: 'currency',
        valueGetter: m => ({amount: m.pokerCashPloCommissionableAmount, currency}),
        field: 'pokerCashPloCommissionableAmount',
        title: localized.pokerCashPloCommissionableAmount,
        sortable: true,
    };
    const pokerCashPloCosts: CurrencyColumn<AgentLevelReportV2ViewModel> = {
        ...defaultColumn,
        type: 'currency',
        valueGetter: m => ({amount: m.pokerCashPloCosts, currency}),
        field: 'pokerCashPloCosts',
        title: localized.pokerCashPloCosts,
        sortable: true,
    };
    const pokerCashPloGgr: CurrencyColumn<AgentLevelReportV2ViewModel> = {
        ...defaultColumn,
        type: 'currency',
        valueGetter: m => ({amount: m.pokerCashPloGgr, currency}),
        field: 'pokerCashPloGgr',
        title: localized.pokerCashPloGgr,
        sortable: true,
    };
    const pokerCashPloNetShare: CurrencyColumn<AgentLevelReportV2ViewModel> = {
        ...defaultColumn,
        width: 200,
        type: 'currency',
        valueGetter: m => ({amount: m.pokerCashPloNetShare, currency}),
        field: 'pokerCashPloNetShare',
        title: localized.pokerCashPloNetShare,
        sortable: true,
    };
    const pokerCashPloNgr: CurrencyColumn<AgentLevelReportV2ViewModel> = {
        ...defaultColumn,
        type: 'currency',
        valueGetter: m => ({amount: m.pokerCashPloNgr, currency}),
        field: 'pokerCashPloNgr',
        title: localized.pokerCashPloNgr,
        sortable: true,
    };
    const pokerCashPloShareRatioPerc: CurrencyColumn<AgentLevelReportV2ViewModel> = {
        ...defaultColumn,
        type: 'currency',
        valueGetter: m => ({amount: m.pokerCashPloShareRatioPerc, currency}),
        field: 'pokerCashPloShareRatioPerc',
        title: localized.pokerCashPloShareRatioPerc,
        sortable: true,
    };
    const pokerMttCommissionableAmount: CurrencyColumn<AgentLevelReportV2ViewModel> = {
        ...defaultColumn,
        type: 'currency',
        valueGetter: m => ({amount: m.pokerMttCommissionableAmount, currency}),
        field: 'pokerMttCommissionableAmount',
        title: localized.pokerMttCommissionableAmount,
        sortable: true,
    };
    const pokerMttCosts: CurrencyColumn<AgentLevelReportV2ViewModel> = {
        ...defaultColumn,
        type: 'currency',
        valueGetter: m => ({amount: m.pokerMttCosts, currency}),
        field: 'pokerMttCosts',
        title: localized.pokerMttCosts,
        sortable: true,
    };
    const pokerMttGgr: CurrencyColumn<AgentLevelReportV2ViewModel> = {
        ...defaultColumn,
        type: 'currency',
        valueGetter: m => ({amount: m.pokerMttGgr, currency}),
        field: 'pokerMttGgr',
        title: localized.pokerMttGgr,
        sortable: true,
    };
    const pokerMttNetShare: CurrencyColumn<AgentLevelReportV2ViewModel> = {
        ...defaultColumn,
        type: 'currency',
        valueGetter: m => ({amount: m.pokerMttNetShare, currency}),
        field: 'pokerMttNetShare',
        title: localized.pokerMttNetShare,
        sortable: true,
    };
    const pokerMttNgr: CurrencyColumn<AgentLevelReportV2ViewModel> = {
        ...defaultColumn,
        type: 'currency',
        valueGetter: m => ({amount: m.pokerMttNgr, currency}),
        field: 'pokerMttNgr',
        title: localized.pokerMttNgr,
        sortable: true,
    };
    const pokerMttShareRatioPerc: CurrencyColumn<AgentLevelReportV2ViewModel> = {
        ...defaultColumn,
        type: 'currency',
        valueGetter: m => ({amount: m.pokerMttShareRatioPerc, currency}),
        field: 'pokerMttShareRatioPerc',
        title: localized.pokerMttShareRatioPerc,
        sortable: true,
    };
    const pokerNlheSdCommissionableAmount: CurrencyColumn<AgentLevelReportV2ViewModel> = {
        ...defaultColumn,
        type: 'currency',
        valueGetter: m => ({amount: m.pokerNlheSdCommissionableAmount, currency}),
        field: 'pokerNlheSdCommissionableAmount',
        title: localized.pokerNlheSdCommissionableAmount,
        sortable: true,
    };
    const pokerNlheSdCosts: CurrencyColumn<AgentLevelReportV2ViewModel> = {
        ...defaultColumn,
        type: 'currency',
        valueGetter: m => ({amount: m.pokerNlheSdCosts, currency}),
        field: 'pokerNlheSdCosts',
        title: localized.pokerNlheSdCosts,
        sortable: true,
    };
    const pokerNlheSdGgr: CurrencyColumn<AgentLevelReportV2ViewModel> = {
        ...defaultColumn,
        type: 'currency',
        valueGetter: m => ({amount: m.pokerNlheSdGgr, currency}),
        field: 'pokerNlheSdGgr',
        title: localized.pokerNlheSdGgr,
        sortable: true,
    };
    const pokerNlheSdNetShare: CurrencyColumn<AgentLevelReportV2ViewModel> = {
        ...defaultColumn,
        width: 200,
        type: 'currency',
        valueGetter: m => ({amount: m.pokerNlheSdNetShare, currency}),
        field: 'pokerNlheSdNetShare',
        title: localized.pokerNlheSdNetShare,
        sortable: true,
    };
    const pokerNlheSdNgr: CurrencyColumn<AgentLevelReportV2ViewModel> = {
        ...defaultColumn,
        type: 'currency',
        valueGetter: m => ({amount: m.pokerNlheSdNgr, currency}),
        field: 'pokerNlheSdNgr',
        title: localized.pokerNlheSdNgr,
        sortable: true,
    };
    const pokerNlheSdShareRatioPerc: CurrencyColumn<AgentLevelReportV2ViewModel> = {
        ...defaultColumn,
        type: 'currency',
        valueGetter: m => ({amount: m.pokerNlheSdShareRatioPerc, currency}),
        field: 'pokerNlheSdShareRatioPerc',
        title: localized.pokerNlheSdShareRatioPerc,
        sortable: true,
    };
    const rowId: TextColumn<AgentLevelReportV2ViewModel> = {
        ...defaultColumn,
        type: 'text',
        valueGetter: m => m.rowId,
        field: 'rowId',
        title: localized.rowId,
        sortable: true,
    };
    const sportsCommissionableAmount: CurrencyColumn<AgentLevelReportV2ViewModel> = {
        ...defaultColumn,
        type: 'currency',
        valueGetter: m => ({amount: m.sportsCommissionableAmount, currency}),
        field: 'sportsCommissionableAmount',
        title: localized.sportsCommissionableAmount,
        sortable: true,
    };
    const sportsCosts: CurrencyColumn<AgentLevelReportV2ViewModel> = {
        ...defaultColumn,
        type: 'currency',
        valueGetter: m => ({amount: m.sportsCosts, currency}),
        field: 'sportsCosts',
        title: localized.sportsCosts,
        sortable: true,
    };
    const sportsGgr: CurrencyColumn<AgentLevelReportV2ViewModel> = {
        ...defaultColumn,
        type: 'currency',
        valueGetter: m => ({amount: m.sportsGgr, currency}),
        field: 'sportsGgr',
        title: localized.sportsGgr,
        sortable: true,
    };
    const sportsNetShare: CurrencyColumn<AgentLevelReportV2ViewModel> = {
        ...defaultColumn,
        type: 'currency',
        valueGetter: m => ({amount: m.sportsNetShare, currency}),
        field: 'sportsNetShare',
        title: localized.sportsNetShare,
        sortable: true,
    };
    const sportsNgr: CurrencyColumn<AgentLevelReportV2ViewModel> = {
        ...defaultColumn,
        type: 'currency',
        valueGetter: m => ({amount: m.sportsNgr, currency}),
        field: 'sportsNgr',
        title: localized.sportsNgr,
        sortable: true,
    };
    const sportsShareRatioPerc: CurrencyColumn<AgentLevelReportV2ViewModel> = {
        ...defaultColumn,
        type: 'currency',
        valueGetter: m => ({amount: m.sportsShareRatioPerc, currency}),
        field: 'sportsShareRatioPerc',
        title: localized.sportsShareRatioPerc,
        sortable: true,
    };
    const totalNetShare: CurrencyColumn<AgentLevelReportV2ViewModel> = {
        ...defaultColumn,
        width: 200,
        type: 'currency',
        valueGetter: m => ({amount: m.totalNetShare, currency}),
        field: 'totalNetShare',
        title: localized.totalNetShare,
        sortable: true,
    };
    const weekStartDateTs: TextColumn<AgentLevelReportV2ViewModel> = {
        ...defaultColumn,
        width: 200,
        type: 'text',
        valueGetter: m => formatTimestamp(m.weekStartDateTs, 'date'),
        field: 'weekStartDateTs',
        title: localized.weekStartDateTs,
        sortable: true,
    };
    const {openModal} = useContext(ModalContext);
    const {formatMessage} = useIntl();
    const details: ButtonColumn<AgentLevelReportV2ViewModel> = {
        ...defaultColumn,
        type: 'button',
        valueGetter: m => ({
            title: formatMessage(localized.details),
            onClick: () =>
                openModal({
                    body: <RevenueShareDetails rowId={m.rowId} agentUid={m.agentUid} type={type} startDate={startDate} endDate={endDate} />,
                    styleParams: {
                        maxWidth: 'lg',
                    },
                }),
        }),
        title: localized.details,
        field: 'details',
        sortable: false,
    };
    const config: Record<DataGridAgentLevelReportV2Keys, DataGridColumn<AgentLevelReportV2ViewModel>> = {
        agentUid,
        agentUsername,
        casinoCommissionableAmount,
        casinoCosts,
        casinoGgr,
        casinoNetShare,
        casinoNgr,
        casinoShareRatioPerc,
        globalSpinsCommissionableAmount,
        globalSpinsCosts,
        globalSpinsGgr,
        globalSpinsNetShare,
        globalSpinsNgr,
        globalSpinsShareRatioPerc,
        pokerCashPloCommissionableAmount,
        pokerCashPloCosts,
        pokerCashPloGgr,
        pokerCashPloNetShare,
        pokerCashPloNgr,
        pokerCashPloShareRatioPerc,
        pokerMttCommissionableAmount,
        pokerMttCosts,
        pokerMttGgr,
        pokerMttNetShare,
        pokerMttNgr,
        pokerMttShareRatioPerc,
        pokerNlheSdCommissionableAmount,
        pokerNlheSdCosts,
        pokerNlheSdGgr,
        pokerNlheSdNetShare,
        pokerNlheSdNgr,
        pokerNlheSdShareRatioPerc,
        rowId,
        sportsCommissionableAmount,
        sportsCosts,
        sportsGgr,
        sportsNetShare,
        sportsNgr,
        sportsShareRatioPerc,
        totalNetShare,
        weekStartDateTs,
        details,
    };

    const columns = React.useMemo(() => {
        return keys.map(k => config[k]);
    }, [keys.join(','), startDate, endDate]);
    return columns;
}

type DataGridAgentLevelReportV2Props = Omit<DataGridServerProps<AgentLevelReportV2ViewModel, DataGridAgentLevelReportV2Keys>, 'columns'> & {
    columns: DataGridAgentLevelReportV2Keys[];
    type?: QueryType;
    startDate?: number;
    endDate?: number;
};

export function DataGridAgentLevelReportV2(props: DataGridAgentLevelReportV2Props) {
    const {columns: columnKeys, type, startDate, endDate} = props;
    const columns = useDataGridAgentLevelReportV2Columns(columnKeys, type, startDate, endDate);

    return <DataGridServer {...props} columns={columns} />;
}
